/**
 * Neglected-alert — top-20 circle people with no contact >30 days.
 * Includes native iOS reach-out deep links: sms://, whatsapp://send, mailto:.
 */
import { store } from '../store.ts';
import { RELATIONSHIP_DISPLAY, relativeContact, daysSince } from '../types.ts';
import type { Person } from '../types.ts';

const CHANNEL_LINK: Record<string, (name: string) => string> = {
  call: (name) => `tel:`,
  whatsapp: (name) => `whatsapp://send?text=${encodeURIComponent('Hey ' + name.split(' ')[0] + '! kab mil rahe hain?')}`,
  imessage: (name) => `sms:&body=${encodeURIComponent('Hey ' + name.split(' ')[0] + '! long time, kya plan hai?')}`,
  meet: (name) => `https://meet.google.com/new`,
  mail: (name) => `mailto:?subject=${encodeURIComponent('catching up')}&body=${encodeURIComponent('Hey ' + name.split(' ')[0] + ',\n\nLong time. Coffee?\n\n')}`,
  instagram: (name) => `https://instagram.com`,
};

function linkFor(channel: string | null, name: string): string {
  if (!channel) return '#';
  const fn = CHANNEL_LINK[channel];
  return fn ? fn(name) : '#';
}

function channelLabel(channel: string | null): string {
  if (!channel) return 'reach out';
  return { call: 'Call', whatsapp: 'WhatsApp', imessage: 'Message', meet: 'Meet', mail: 'Email', instagram: 'Insta DM' }[channel] ?? 'reach';
}

export class DialNeglectedAlert extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.inner_circle.slice(0, 20));
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(top20: Person[]): void {
    const neglected = top20
      .map((p) => ({ p, days: daysSince(p.last_contact_iso) ?? 0 }))
      .filter((x) => x.days >= 30)
      .sort((a, b) => b.days - a.days)
      .slice(0, 6);

    const body = neglected.length === 0
      ? `<div class="empty">Saare yaar warm. Top 20 mein koi 30 din se zyada gayab nahi hai.</div>`
      : neglected.map(({ p, days }) => /* html */ `
<div class="row">
  <div class="who">
    <div class="name">${escapeHtml(p.name)}</div>
    <div class="meta">${RELATIONSHIP_DISPLAY[p.relationship_kind]} · ${escapeHtml(p.city ?? '—')} · <span class="days">${days} days quiet</span></div>
  </div>
  <a class="cta" href="${linkFor(p.last_contact_channel, p.name)}">${channelLabel(p.last_contact_channel)} →</a>
</div>`).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host {
  display: block;
  view-transition-name: neglected-alert;
}
.title { font-family: var(--mono); font-size: 10px; letter-spacing: 0.42em; text-transform: uppercase; color: var(--ink-faint); margin-bottom: 14px; padding-left: 4px; }
.box {
  background: var(--paper-up);
  border: 1px solid var(--rule);
  border-radius: 8px;
  border-left: 3px solid var(--rose);
  overflow: hidden;
  position: relative;
}
.box::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(155deg, oklch(78% 0.10 22 / 0.12), transparent 50%);
  pointer-events: none;
}
.row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid var(--rule);
  transition: background 220ms cubic-bezier(0.32, 0.72, 0, 1);
  position: relative;
}
.row:hover {
  background: color-mix(in oklch, var(--rose) 8%, var(--paper-up));
}
.row:last-child { border-bottom: none; }
.who { min-width: 0; }
.name { font-family: var(--display); font-size: 20px; color: var(--ink); font-weight: 600; letter-spacing: -0.008em; text-wrap: balance; }
.meta { font-family: var(--mono); font-size: 10px; color: var(--ink-faint); letter-spacing: 0.05em; text-transform: lowercase; margin-top: 5px; }
.days { color: var(--rose); font-feature-settings: 'tnum' 1; }
.cta {
  font-family: var(--mono);
  font-size: 10px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 11px 15px;
  border: 1px solid var(--rule);
  border-radius: 6px;
  color: var(--maroon);
  background: var(--paper);
  white-space: nowrap;
  transition: background 180ms cubic-bezier(0.32, 0.72, 0, 1), border-color 180ms cubic-bezier(0.32, 0.72, 0, 1);
}
.cta:hover {
  background: color-mix(in oklch, var(--maroon) 8%, var(--paper));
  border-color: var(--maroon);
}
.cta:active { background: var(--peach); }
.empty {
  padding: 22px 18px;
  font-family: var(--display);
  font-style: italic;
  color: var(--ink-dim);
  font-size: 18px;
  text-wrap: pretty;
  position: relative;
}
</style>
<div class="title">Reach out — top-20 ke kaun gayab</div>
<div class="box">${body}</div>
`;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('dial-neglected-alert', DialNeglectedAlert);
